import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { Navigate } from "react-router-dom";
import { useOrganization } from "./OrganizationContext";
import { getFeatureFlags } from "../modules/upload-inbox/api";

/**
 * Tester feature flags. The MVP exposes Documents, Upload and AI renaming
 * to everyone; everything below is off by default and unlocked per org or
 * per user from the admin console (Feature Flags page).
 *
 * The server is the source of truth (GET /api/features) and enforces the
 * same flags on the API and in the worker — hiding UI here is UX only.
 *
 * Keep in sync with services/api/src/services/feature-flags.ts.
 */
export const FEATURE_KEYS = [
  "dashboard",
  "activity_log",
  "team",
  "settings_page",
  "ai_tagging",
  "event_detection",
  "ai_split_suggestions",
  "pdf_split",
  "cloud_import",
] as const;

export type FeatureKey = (typeof FEATURE_KEYS)[number];
export type FeatureFlags = Record<FeatureKey, boolean>;

/** MVP defaults: every flag off. Also the fail-closed fallback. */
export const MVP_FLAGS: FeatureFlags = Object.fromEntries(
  FEATURE_KEYS.map((k) => [k, false]),
) as FeatureFlags;

interface FeatureFlagsValue {
  flags: FeatureFlags;
  /** False until the first fetch for the active org settles. */
  loaded: boolean;
}

const FeatureFlagsContext = createContext<FeatureFlagsValue | null>(null);

/** Coerce a server payload to a full flag map; unknown/missing keys are off. */
export function normalizeFlags(raw: unknown): FeatureFlags {
  const src = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
  return Object.fromEntries(FEATURE_KEYS.map((k) => [k, src[k] === true])) as FeatureFlags;
}

/**
 * Loads effective flags for the current user + active org, refetching on
 * org switch (overrides can be per-org). Render inside ContextGuard so
 * auth and org context are ready.
 */
export function FeatureFlagsProvider({ children }: { children: ReactNode }) {
  const { activeOrganization } = useOrganization();
  const orgId = activeOrganization?.id ?? null;
  const [state, setState] = useState<FeatureFlagsValue>({ flags: MVP_FLAGS, loaded: false });

  useEffect(() => {
    let cancelled = false;
    setState((s) => ({ ...s, loaded: false }));
    getFeatureFlags()
      .then((res) => {
        if (!cancelled) setState({ flags: normalizeFlags(res.flags), loaded: true });
      })
      .catch((err) => {
        console.error("Failed to load feature flags; using MVP defaults", err);
        if (!cancelled) setState({ flags: MVP_FLAGS, loaded: true });
      });
    return () => {
      cancelled = true;
    };
  }, [orgId]);

  return <FeatureFlagsContext.Provider value={state}>{children}</FeatureFlagsContext.Provider>;
}

/** Fixed flags for tests and storybook-style rendering. */
export function StaticFeatureFlagsProvider({
  flags,
  children,
}: {
  flags: Partial<FeatureFlags>;
  children: ReactNode;
}) {
  return (
    <FeatureFlagsContext.Provider value={{ flags: { ...MVP_FLAGS, ...flags }, loaded: true }}>
      {children}
    </FeatureFlagsContext.Provider>
  );
}

/** Without a provider every flag reads as off (MVP behaviour). */
export function useFeatureFlags(): FeatureFlagsValue {
  return useContext(FeatureFlagsContext) ?? { flags: MVP_FLAGS, loaded: true };
}

export function useFeature(key: FeatureKey): boolean {
  return useFeatureFlags().flags[key];
}

/** Landing route for anything gated off — Documents is always MVP. */
export const FALLBACK_ROUTE = "/documents";

/**
 * Route guard: renders children when `feature` is on, otherwise redirects
 * to Documents. Renders nothing while flags are still loading so a
 * flagged page never flashes before the redirect.
 */
export function FeatureRoute({ feature, children }: { feature: FeatureKey; children: ReactNode }) {
  const { flags, loaded } = useFeatureFlags();
  if (!loaded) return null;
  if (!flags[feature]) return <Navigate to={FALLBACK_ROUTE} replace />;
  return <>{children}</>;
}
